/**
 * THE ONE PLACE PORTFOLIO MATH HAPPENS.
 *
 * <h2>The defect this exists to end</h2>
 *
 * The Running Ledger and the DynaMan Simulator reported the same trades — identical count, wins,
 * losses and profit factor — and different Net PnL, Equity and Max Drawdown, because each
 * carried its own arithmetic:
 *
 * <pre>
 *   Simulator (LedgerStatistics.java)   equity *= (1 + pnl/100)      COMPOUNDED
 *   Running Ledger (replayStats.js)     grossProfit += pnl            SUMMED
 * </pre>
 *
 * Profit factor agreed by accident — it is a ratio of arithmetic sums on both sides, so it was
 * the one figure the two happened to share, which made the disagreement look smaller than it was.
 *
 * <h2>COMPOUNDED IS THE SOURCE OF TRUTH, and summing is simply wrong</h2>
 *
 * Adding returns taken on different bases does not produce a return. +10% then -10% is a 1%
 * LOSS, not zero; over 146 trades the two answers are not close. Every published figure in
 * CLAUDE.md is the compounded one, so this is also the choice that leaves the existing research
 * comparable rather than silently re-baselining twenty measurements.
 *
 * <h2>Dollars ARE summed, and that is not the same mistake</h2>
 *
 * Worth stating because it looks like one. Each trade's {@code netPnl} already comes from
 * {@code AccountSimulation}, which sizes that trade against the equity standing when it was
 * taken — so the compounding is already inside each figure, and
 * {@code startingEquity + sum(netPnl)} is the compounded result rather than an approximation of
 * it. Compounding them again would count it twice.
 *
 * <h2>Incremental by construction</h2>
 *
 * The Running Ledger needs these figures at every frame of a replay, not just at the end, so the
 * reducer is the primitive and the whole-list function is a fold over it. One implementation
 * serves both; a separate "streaming" version is exactly how the two drifted apart the first
 * time.
 */

/** A fresh accumulator. Equity starts at 1.0 so the curve is a pure growth factor. */
export const EMPTY_PORTFOLIO = Object.freeze({
  tradeCount: 0,
  wins: 0,
  losses: 0,

  // ── the compounded percentage track — the source of truth ───────────────────────────────
  /** The growth factor, NOT a percentage. 1.0 is flat; 1.5 is up 50%. */
  equityFactor: 1,
  peakFactor: 1,
  maxDrawdownPercent: 0,

  // Gross gain and gross loss kept APART, never netted: "up 40k" and "made 180k, gave back
  // 140k" are the same net and completely different books.
  grossProfitPercent: 0,
  grossLossPercent: 0,
  winTotalPercent: 0,
  lossTotalPercent: 0,

  rTotal: 0,
  // THE WINNERS' R, accumulated apart from rTotal. Averaging R over every trade gives
  // EXPECTANCY; the asymmetry ratio a 4:1 book claims is the winners' mean alone, and a book at
  // 4:1 with a 25% win rate has an expectancy near zero R. Two accumulators because they are two
  // quantities, which is exactly how LedgerStatistics.java keeps them.
  winnerRTotal: 0,
  pointsTotal: 0,
  daysTotal: 0,
  bestPercent: null,
  worstPercent: null,

  // ── the sized dollar track, present only when the run carried an account ────────────────
  /** Null until a trade carries a dollar figure — 0 would claim a flat book. */
  netMoney: null,
  grossProfitMoney: 0,
  grossLossMoney: 0,
  peakEquityMoney: null,
  maxDrawdownMoneyPercent: 0,
});

/** Gross gains over gross losses. NULL with no losses — an absent denominator, not a good score. */
export function profitFactorOf(grossProfitPercent, grossLossPercent) {
  return grossLossPercent > 0 ? grossProfitPercent / grossLossPercent : null;
}

const num = (v) => (Number.isFinite(v) ? v : 0);

/**
 * Folds one closed trade into the accumulator.
 *
 * @param acc            the previous accumulator, or {@link EMPTY_PORTFOLIO}
 * @param trade          needs {@code pnlPercent}; {@code netPnl}, {@code rMultiple} and
 *                       {@code tradingDays} are used when present
 * @param initialCapital the account's starting equity, or null for an unsized run
 */
/**
 * A trade's signed result in PRICE POINTS, derived from fields the DTO already carries.
 *
 * <p>Derived rather than shipped, and that is NOT the "two implementations of one quantity"
 * failure this module exists to prevent. That rule is about AGGREGATES -- the Running Ledger
 * summing percentages while the Simulator compounded them. This is a single trade's
 * {@code (exit - entry) x sign}, an identity over three numbers already on the wire, and it is
 * written ONCE here so the ledger table and the statistics tiles cannot differ.
 */
export function pointsOf(trade) {
  if (!trade) return 0;
  const sign = trade.direction === 'SHORT' ? -1 : 1;
  const points = (num(trade.exitPrice) - num(trade.entryPrice)) * sign;
  return Number.isFinite(points) ? points : 0;
}

/** The initial risk in POINTS: entry to the stop that was in force. */
export function riskPointsOf(trade) {
  if (!trade) return 0;
  const risk = Math.abs(num(trade.entryPrice) - num(trade.initialStop));
  return Number.isFinite(risk) ? risk : 0;
}

export function accumulateTrade(acc, trade, initialCapital = null) {
  const base = acc || EMPTY_PORTFOLIO;
  const pnl = num(trade && trade.pnlPercent);

  // STRICTLY greater than zero is a win. A scratch claimed a move and returned nothing, and
  // TradeRecord.isWin applies the same rule — a HUD that disagreed with the final report about
  // what counts as a win would make the two impossible to reconcile.
  const win = pnl > 0;

  // THE COMPOUNDED CURVE. Path-dependent, so the trade order is load-bearing and this cannot be
  // derived from the totals: the totals cannot tell a steady climb from a round trip.
  const equityFactor = base.equityFactor * (1 + pnl / 100);
  const peakFactor = Math.max(base.peakFactor, equityFactor);
  const maxDrawdownPercent = peakFactor > 0
    ? Math.max(base.maxDrawdownPercent, ((peakFactor - equityFactor) / peakFactor) * 100)
    : base.maxDrawdownPercent;

  // THE DOLLAR CURVE, when the trade carries one. Summed, because each netPnl was already sized
  // against the equity standing at its own entry — see the header.
  const money = Number.isFinite(trade && trade.netPnl) ? trade.netPnl : null;
  const netMoney = money === null
    ? base.netMoney
    : num(base.netMoney) + money;

  let peakEquityMoney = base.peakEquityMoney;
  let maxDrawdownMoneyPercent = base.maxDrawdownMoneyPercent;
  if (money !== null && Number.isFinite(initialCapital)) {
    const equity = initialCapital + netMoney;
    const peak = Math.max(
      peakEquityMoney === null || peakEquityMoney === undefined ? initialCapital : peakEquityMoney,
      equity,
    );
    peakEquityMoney = peak;
    if (peak > 0) {
      maxDrawdownMoneyPercent = Math.max(maxDrawdownMoneyPercent, ((peak - equity) / peak) * 100);
    }
  }

  return {
    tradeCount: base.tradeCount + 1,
    wins: base.wins + (win ? 1 : 0),
    losses: base.tradeCount + 1 - (base.wins + (win ? 1 : 0)),

    equityFactor,
    peakFactor,
    maxDrawdownPercent,

    grossProfitPercent: base.grossProfitPercent + (win ? pnl : 0),
    grossLossPercent: base.grossLossPercent + (win ? 0 : Math.abs(pnl)),
    winTotalPercent: base.winTotalPercent + (win ? pnl : 0),
    lossTotalPercent: base.lossTotalPercent + (win ? 0 : pnl),

    rTotal: base.rTotal + num(trade && trade.rMultiple),
    winnerRTotal: base.winnerRTotal + (win ? num(trade && trade.rMultiple) : 0),
    pointsTotal: base.pointsTotal + pointsOf(trade),
    daysTotal: base.daysTotal + num(trade && trade.tradingDays),
    bestPercent: base.bestPercent === null ? pnl : Math.max(base.bestPercent, pnl),
    worstPercent: base.worstPercent === null ? pnl : Math.min(base.worstPercent, pnl),

    netMoney,
    grossProfitMoney: base.grossProfitMoney + (money !== null && money > 0 ? money : 0),
    grossLossMoney: base.grossLossMoney + (money !== null && money < 0 ? Math.abs(money) : 0),
    peakEquityMoney,
    maxDrawdownMoneyPercent,
  };
}

/**
 * Turns an accumulator into the figures a UI renders.
 *
 * <p>Field names match {@code LedgerStatistics} exactly, so a panel reading the backend's block
 * and one reading this cannot render different keys for the same quantity.
 */
export function finalisePortfolio(acc, initialCapital = null) {
  const a = acc || EMPTY_PORTFOLIO;
  if (a.tradeCount === 0) {
    return {
      tradeCount: 0,
      wins: 0,
      losses: 0,
      winRatePercent: 0,
      totalNetPnlPercent: 0,
      profitFactor: null,
      averageWinPercent: 0,
      averageLossPercent: 0,
      // DEEPEST PEAK-TO-TROUGH of the trade-sequence curve.
      maxDrawdownPercent: 0,
      averageRMultiple: 0,
      totalGrossPoints: 0,
      averageRewardToRisk: null,
      payoffRatio: null,
      bestPercent: 0,
      worstPercent: 0,
      averageTradingDays: 0,
      grossProfitPercent: 0,
      grossLossPercent: 0,
      // NULL, not zero: before the first trade there is no curve, and a zero peak would make
      // the first loss read as a 100% drawdown.
      endingEquity: Number.isFinite(initialCapital) ? initialCapital : null,
      netMoney: null,
      grossProfitMoney: 0,
      grossLossMoney: 0,
      peakEquity: null,
      maxDrawdownMoneyPercent: 0,
      sized: false,
    };
  }

  const sized = a.netMoney !== null && Number.isFinite(initialCapital);
  return {
    tradeCount: a.tradeCount,
    wins: a.wins,
    losses: a.losses,
    winRatePercent: (100 * a.wins) / a.tradeCount,

    // THE HEADLINE, compounded. (equityFactor - 1) * 100, exactly as LedgerStatistics computes it.
    totalNetPnlPercent: (a.equityFactor - 1) * 100,
    profitFactor: profitFactorOf(a.grossProfitPercent, a.grossLossPercent),
    averageWinPercent: a.wins === 0 ? 0 : a.winTotalPercent / a.wins,
    averageLossPercent: a.losses === 0 ? 0 : a.lossTotalPercent / a.losses,
    maxDrawdownPercent: a.maxDrawdownPercent,
    averageRMultiple: a.rTotal / a.tradeCount,

    // SUMMED, not compounded -- points are currency, not a rate. See TradeRecord.points for why
    // a sum over decades of a 15x price range weights the recent years enormously.
    totalGrossPoints: a.pointsTotal,
    // NULL rather than zero when the denominator is absent, the rule profit factor already
    // follows. Zero is a real reading -- winners that gave back exactly their risk.
    averageRewardToRisk: a.wins === 0 ? null : a.winnerRTotal / a.wins,
    payoffRatio: a.losses === 0 || a.lossTotalPercent === 0
      ? null
      : (a.winTotalPercent / Math.max(a.wins, 1))
        / Math.abs(a.lossTotalPercent / a.losses),
    bestPercent: a.bestPercent === null ? 0 : a.bestPercent,
    worstPercent: a.worstPercent === null ? 0 : a.worstPercent,
    averageTradingDays: a.daysTotal / a.tradeCount,

    grossProfitPercent: a.grossProfitPercent,
    grossLossPercent: a.grossLossPercent,

    endingEquity: sized ? initialCapital + a.netMoney : null,
    netMoney: a.netMoney,
    grossProfitMoney: a.grossProfitMoney,
    grossLossMoney: a.grossLossMoney,
    peakEquity: a.peakEquityMoney,
    maxDrawdownMoneyPercent: a.maxDrawdownMoneyPercent,
    sized,
  };
}

/**
 * THE ENTRY POINT. Every portfolio figure in the app, from one list of closed trades.
 *
 * @param trades         closed trades, IN THE ORDER THEY WERE TAKEN. Drawdown is path-dependent,
 *                       so a re-sorted list produces a different and wrong answer
 * @param initialCapital the account's starting equity, or null/undefined for an unsized run
 * @param positionSizing reserved — the sizing already happened server-side, inside each trade's
 *                       {@code netPnl}. Accepted so the signature is stable if sizing ever moves
 *                       to the client, and IGNORED today rather than silently half-applied
 */
export function calculatePortfolioMetrics(trades, initialCapital = null, positionSizing = null) {
  const list = Array.isArray(trades) ? trades : [];
  const capital = Number.isFinite(initialCapital) ? initialCapital : null;

  let acc = EMPTY_PORTFOLIO;
  list.forEach((t) => { acc = accumulateTrade(acc, t, capital); });

  const out = finalisePortfolio(acc, capital);
  // Carried through so a caller can see what it asked for; never used to alter the arithmetic.
  return positionSizing ? { ...out, positionSizing } : out;
}

/**
 * The drawdown a UI should show.
 *
 * <p>Prefers the CURRENCY curve when the run was sized, because that is the fall a reader of a
 * dollar book actually experienced. Falls back to the compounded percentage curve otherwise —
 * which is the fix for an unsized replay reporting a flat <b>0.00%</b> drawdown, a figure that
 * reads as "riskless" and was simply the absence of a dollar curve to measure.
 */
export function displayDrawdownPercent(metrics) {
  if (!metrics || metrics.tradeCount === 0) return 0;
  return metrics.sized ? metrics.maxDrawdownMoneyPercent : metrics.maxDrawdownPercent;
}

/* ===============================================================================================
 * INSTITUTIONAL RISK RATIOS
 * ===============================================================================================
 *
 * <h2>THE APPROXIMATION EVERY ONE OF THESE RESTS ON, stated once and loudly</h2>
 *
 * Sharpe, Sortino and the Information Ratio are defined over a series of returns measured at a
 * FIXED interval — daily, monthly. A trade list is not that. Trades have different durations and
 * gaps between them, so treating each trade as one "period" gives every trade equal weight in the
 * volatility whatever its length: a three-week hold and a one-day scalp count the same.
 *
 * <p>That is the standard trade-based formulation and it is what these functions compute, but it
 * is an approximation and the figures are therefore NOT comparable with a fund's published Sharpe
 * computed from monthly returns. The honest reading is "Sharpe over this strategy's own trade
 * sequence". A bar-by-bar equity curve would be needed for the other thing, and a list of closed
 * trades cannot support it.
 *
 * <h2>Annualised by REAL ELAPSED TIME, never by trade count</h2>
 *
 * The periods-per-year factor comes from the calendar span between the first entry and the last
 * exit. Using a nominal 252 or the trade count alone would give ten trades over thirty years the
 * same annualisation as ten over thirty days, which is not a smaller error — it is a different
 * answer.
 *
 * <p>Timestamps are parsed to epoch millis here, and that is correct even though CLAUDE.md warns
 * against parsing engine timestamps with {@code Date}. That warning is about mapping an instant to
 * a CALENDAR DATE, where the exchange's offset shifts the day. A DIFFERENCE between two instants
 * is offset-independent, which is the opposite case.
 *
 * <h2>Everything degenerate returns NULL, never NaN and never Infinity</h2>
 *
 * A ratio with a zero denominator is undefined, and rendering it as Infinity — or worse, as a
 * very large finite number after some later arithmetic — is how "no losing trades" comes to read
 * as a spectacular score. This file already applies that rule to profit factor; these follow it.
 */

/** Annual risk-free rate as a FRACTION, not a percentage. 4%. */
export const DEFAULT_RISK_FREE_RATE = 0.04;

/** Trades below this count cannot support a dispersion estimate worth reporting. */
export const MIN_TRADES_FOR_RATIOS = 3;

const MS_PER_YEAR = 365.2425 * 24 * 60 * 60 * 1000;

/** A finite number, or null. The single gate everything returned here passes through. */
const finiteOrNull = (v) => (Number.isFinite(v) ? v : null);

/** Sample standard deviation (n-1). Null below two observations, where it is undefined. */
export function sampleStdDev(values) {
  const xs = (values || []).filter(Number.isFinite);
  if (xs.length < 2) return null;
  const mean = xs.reduce((a, b) => a + b, 0) / xs.length;
  const sumSq = xs.reduce((a, b) => a + (b - mean) * (b - mean), 0);
  return finiteOrNull(Math.sqrt(sumSq / (xs.length - 1)));
}

/**
 * The span the trades actually cover, in years.
 *
 * <p>From the first ENTRY to the last EXIT, because that is the period capital was committed
 * over. Falls back to the summed holding days over 252 when timestamps are missing or unusable —
 * a weaker figure, but it keeps a synthetic fixture from silently annualising by zero.
 */
export function yearsSpanned(trades) {
  const list = Array.isArray(trades) ? trades : [];
  if (list.length === 0) return null;

  const stamps = [];
  list.forEach((t) => {
    [t && t.entryTime, t && t.exitTime].forEach((s) => {
      if (typeof s !== 'string' || !s) return;
      const ms = Date.parse(s);
      if (Number.isFinite(ms)) stamps.push(ms);
    });
  });

  if (stamps.length >= 2) {
    const span = Math.max(...stamps) - Math.min(...stamps);
    if (span > 0) return span / MS_PER_YEAR;
  }

  // 252 trading days to the year. Only reached when the series carries no usable timestamps.
  const days = list.reduce((a, t) => a + (Number.isFinite(t && t.tradingDays) ? t.tradingDays : 0), 0);
  return days > 0 ? days / 252 : null;
}

/**
 * The complete institutional set.
 *
 * @param trades   closed trades IN ORDER, each with {@code pnlPercent}
 * @param options  {@code riskFreeRate} (annual fraction, default 0.04),
 *                 {@code omegaThreshold} (per-period fraction, default 0),
 *                 {@code benchmarkReturns} (per-trade fractions aligned to `trades`, for the
 *                 Information Ratio — omitted means the IR is null, not zero),
 *                 {@code maxDrawdownPercent} (the drawdown Calmar divides by; taken from the
 *                 caller so Calmar cannot disagree with the drawdown shown beside it)
 */
export function institutionalRatios(trades, options = {}) {
  const list = (Array.isArray(trades) ? trades : []).filter(Boolean);
  const rf = Number.isFinite(options.riskFreeRate) ? options.riskFreeRate : DEFAULT_RISK_FREE_RATE;
  const threshold = Number.isFinite(options.omegaThreshold) ? options.omegaThreshold : 0;

  const empty = {
    tradeCount: list.length,
    years: null,
    periodsPerYear: null,
    cagr: null,
    annualisedVolatility: null,
    annualisedDownsideDeviation: null,
    sharpe: null,
    sortino: null,
    calmar: null,
    informationRatio: null,
    trackingError: null,
    omega: null,
    riskFreeRate: rf,
  };
  if (list.length === 0) return empty;

  // Fractions, not percentages, from here down. Mixing the two is the classic way a Sharpe comes
  // out a hundred times too large and still looks plausible.
  const returns = list.map((t) => (Number.isFinite(t.pnlPercent) ? t.pnlPercent / 100 : 0));

  const years = yearsSpanned(list);
  const equityFactor = returns.reduce((e, r) => e * (1 + r), 1);

  // A WIPEOUT HAS NO GROWTH RATE. equityFactor <= 0 means the account went to zero or negative,
  // and the n-th root of a non-positive number is not a return — it is NaN, which would then
  // contaminate Sharpe, Sortino and Calmar together.
  const cagr = (years && years > 0 && equityFactor > 0)
    ? finiteOrNull(equityFactor ** (1 / years) - 1)
    : null;

  const periodsPerYear = (years && years > 0) ? list.length / years : null;
  const out = { ...empty, years, periodsPerYear, cagr };

  // Dispersion needs a sample. Below three trades the standard deviation of two numbers is a
  // statement about those two numbers and nothing else.
  if (list.length < MIN_TRADES_FOR_RATIOS || !periodsPerYear || cagr === null) {
    out.omega = omegaOf(returns, threshold);
    out.calmar = calmarOf(cagr, options.maxDrawdownPercent);
    return out;
  }

  const sd = sampleStdDev(returns);
  const annualisedVolatility = sd === null ? null : finiteOrNull(sd * Math.sqrt(periodsPerYear));
  out.annualisedVolatility = annualisedVolatility;

  // A ZERO DENOMINATOR IS UNDEFINED, not infinite. Every identical return gives sd 0, which is a
  // riskless series by this measure and has no Sharpe.
  out.sharpe = (annualisedVolatility && annualisedVolatility > 0)
    ? finiteOrNull((cagr - rf) / annualisedVolatility)
    : null;

  // DOWNSIDE DEVIATION against the per-period risk-free rate, compounded down from the annual
  // figure rather than divided — (1+rf)^(1/ppy)-1, because rates compound.
  //
  // Divided by N, the count of ALL periods, not by the count of negative ones. That is the
  // standard definition and it is the reason Sortino is not simply "Sharpe with fewer points":
  // a strategy with few but deep losses is penalised less than one that is always slightly down.
  const perPeriodRf = (1 + rf) ** (1 / periodsPerYear) - 1;
  const shortfalls = returns.map((r) => Math.min(r - perPeriodRf, 0));
  const downside = Math.sqrt(shortfalls.reduce((a, s) => a + s * s, 0) / returns.length);
  const annualisedDownside = finiteOrNull(downside * Math.sqrt(periodsPerYear));
  out.annualisedDownsideDeviation = annualisedDownside;

  // NO DOWNSIDE AT ALL means no Sortino. Reporting a huge number for a strategy that never lost
  // is the same error as reporting a huge profit factor for one with no losing trades.
  out.sortino = (annualisedDownside && annualisedDownside > 0)
    ? finiteOrNull((cagr - rf) / annualisedDownside)
    : null;

  out.calmar = calmarOf(cagr, options.maxDrawdownPercent);
  out.omega = omegaOf(returns, threshold);

  // INFORMATION RATIO needs a benchmark, aligned one-for-one with the trades. Absent, it is null
  // and not zero: "no benchmark" and "exactly matched the benchmark" are different findings.
  const bench = Array.isArray(options.benchmarkReturns) ? options.benchmarkReturns : null;
  if (bench && bench.length === returns.length) {
    const excess = returns.map((r, i) => r - (Number.isFinite(bench[i]) ? bench[i] : 0));
    const te = sampleStdDev(excess);
    const annualisedTE = te === null ? null : finiteOrNull(te * Math.sqrt(periodsPerYear));
    out.trackingError = annualisedTE;
    if (annualisedTE && annualisedTE > 0) {
      const meanExcess = excess.reduce((a, b) => a + b, 0) / excess.length;
      out.informationRatio = finiteOrNull((meanExcess * periodsPerYear) / annualisedTE);
    }
  }

  return out;
}

/**
 * CAGR over the absolute maximum drawdown.
 *
 * <p>The drawdown is PASSED IN rather than recomputed, so Calmar cannot disagree with the
 * drawdown rendered beside it — which is exactly the class of defect the one shared reducer in
 * this file exists to prevent.
 *
 * <p>Null on a zero drawdown. A strategy that never gave anything back has an undefined Calmar,
 * not an infinite one, and the difference matters most on the short runs where it happens.
 */
export function calmarOf(cagr, maxDrawdownPercent) {
  if (cagr === null || !Number.isFinite(cagr)) return null;
  if (!Number.isFinite(maxDrawdownPercent)) return null;
  const dd = Math.abs(maxDrawdownPercent) / 100;
  return dd > 0 ? finiteOrNull(cagr / dd) : null;
}

/**
 * Omega: the probability-weighted gains above a threshold over the losses below it.
 *
 * <p>Mathematically the ratio of the areas of the return distribution either side of the
 * threshold, which for a discrete sample is just the sum of the excesses over the sum of the
 * shortfalls. Unlike Sharpe it uses the whole distribution rather than its first two moments, so
 * it says something Sharpe cannot about a skewed book — which is what a stop-truncated,
 * trail-extended trade distribution is.
 *
 * <p>Null when nothing fell below the threshold: an absent denominator, not a perfect score.
 */
export function omegaOf(returns, threshold = 0) {
  const xs = (returns || []).filter(Number.isFinite);
  if (xs.length === 0) return null;
  let above = 0;
  let below = 0;
  xs.forEach((r) => {
    const d = r - threshold;
    if (d > 0) above += d; else below += -d;
  });
  return below > 0 ? finiteOrNull(above / below) : null;
}
